// Pagina gamification — leaderboard, badge utente e obiettivi mensili
import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Trophy, Award, Target } from 'lucide-react'
import { PageHeader } from '../../components/layout/PageHeader'
import { Tabs, TabsContent } from '../../components/ui/Tabs'
import { TechnicianRanking } from './components/TechnicianRanking'
import { BadgeDisplay } from './components/BadgeDisplay'
import { GoalProgress } from './components/GoalProgress'
import type { LeaderboardEntry } from './components/TechnicianRanking'
import api from '../../lib/api'

// ---------------------------------------------------------------
// Tipi risposta API
// ---------------------------------------------------------------

interface UserProgress {
  level: number
  levelName: string
  totalPoints: number
  nextLevelName: string | null
  pointsToNext: number | null
  earnedBadges: Array<{
    id: string
    name: string
    description: string | null
    icon: string | null
    points: number
    earnedAt: string
  }>
  availableBadges: Array<{
    id: string
    name: string
    description: string | null
    icon: string | null
    points: number
    conditionType: string | null
    conditionValue: number | null
  }>
  monthlyGoals: Array<{
    id: string
    label: string
    target: number
    current: number
    unit: string
    progressPercent: number
  }>
}

// ---------------------------------------------------------------
// Definizione tab
// ---------------------------------------------------------------

const TABS = [
  { value: 'leaderboard', label: 'Leaderboard', icon: <Trophy className="h-4 w-4" aria-hidden="true" /> },
  { value: 'badges', label: 'I miei badge', icon: <Award className="h-4 w-4" aria-hidden="true" /> },
  { value: 'goals', label: 'Obiettivi', icon: <Target className="h-4 w-4" aria-hidden="true" /> },
]

const PERIOD_OPTIONS = [
  { value: 'month', label: 'Mese corrente' },
  { value: 'quarter', label: 'Trimestre' },
  { value: 'year', label: 'Anno' },
] as const

type Period = 'month' | 'quarter' | 'year'

// ---------------------------------------------------------------
// Componente principale
// ---------------------------------------------------------------

export function LeaderboardPage() {
  const [activeTab, setActiveTab] = useState('leaderboard')
  const [period, setPeriod] = useState<Period>('month')

  // Query leaderboard
  const {
    data: leaderboard = [],
    isLoading: leaderboardLoading,
  } = useQuery({
    queryKey: ['gamification', 'leaderboard', period],
    queryFn: async () => {
      const res = await api.get<LeaderboardEntry[]>('/gamification/leaderboard', {
        params: { period },
      })
      return res.data
    },
    staleTime: 60_000, // 1 minuto
  })

  // Query progresso utente corrente (badge + goals)
  const {
    data: progress,
    isLoading: progressLoading,
  } = useQuery({
    queryKey: ['gamification', 'me', 'progress'],
    queryFn: async () => {
      const res = await api.get<UserProgress>('/gamification/me/progress')
      return res.data
    },
    staleTime: 120_000, // 2 minuti
    enabled: activeTab === 'badges' || activeTab === 'goals',
  })

  return (
    <div className="space-y-6">
      <PageHeader
        title="Gamification"
        subtitle="Classifica tecnici, badge e obiettivi mensili"
      />

      {/* Banner livello utente — visibile solo se il progresso è caricato */}
      {progress && (
        <div className="flex items-center gap-4 rounded-lg border border-[var(--fc-border)] bg-[var(--fc-surface)] p-4 shadow-[var(--fc-shadow-sm)]">
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-[var(--fc-primary)]/10 text-2xl">
            ⭐
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-[var(--fc-foreground)]">
              {progress.levelName} — Livello {progress.level}
            </p>
            <p className="text-sm text-[var(--fc-foreground-muted)]">
              {progress.totalPoints} punti totali
              {progress.pointsToNext !== null && (
                <> &middot; {progress.pointsToNext} pt al prossimo livello ({progress.nextLevelName})</>
              )}
            </p>
          </div>
          <div className="hidden sm:block text-right">
            <p className="text-xs text-[var(--fc-foreground-muted)]">Badge guadagnati</p>
            <p className="text-xl font-bold text-[var(--fc-primary)]">{progress.earnedBadges.length}</p>
          </div>
        </div>
      )}

      {/* Tabs principali */}
      <Tabs tabs={TABS} value={activeTab} onValueChange={setActiveTab}>

        {/* Tab Leaderboard */}
        <TabsContent value="leaderboard" className="pt-4 space-y-4 focus-visible:outline-none">
          {/* Filtro periodo */}
          <div className="flex items-center gap-2" role="group" aria-label="Filtra per periodo">
            {PERIOD_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                type="button"
                onClick={() => setPeriod(opt.value)}
                className={[
                  'rounded-full px-3 py-1 text-sm font-medium transition-colors',
                  period === opt.value
                    ? 'bg-[var(--fc-primary)] text-white'
                    : 'bg-[var(--fc-gray-200)] text-[var(--fc-foreground)] hover:bg-[var(--fc-gray-300)]',
                ].join(' ')}
                aria-pressed={period === opt.value}
              >
                {opt.label}
              </button>
            ))}
          </div>

          <TechnicianRanking entries={leaderboard} isLoading={leaderboardLoading} />
        </TabsContent>

        {/* Tab Badge */}
        <TabsContent value="badges" className="pt-4 focus-visible:outline-none">
          <BadgeDisplay
            earnedBadges={progress?.earnedBadges ?? []}
            availableBadges={progress?.availableBadges ?? []}
            isLoading={progressLoading}
          />
        </TabsContent>

        {/* Tab Obiettivi */}
        <TabsContent value="goals" className="pt-4 focus-visible:outline-none">
          <GoalProgress
            goals={progress?.monthlyGoals ?? []}
            isLoading={progressLoading}
          />
        </TabsContent>
      </Tabs>
    </div>
  )
}
